import type { CategoryContent } from './row.js';

export const MATERIALS_SCIENCE: CategoryContent = {
  bscba: [
    [
      'What term describes the regular, repeating 3D arrangement of atoms in a crystalline material?',
      [
        'Crystal lattice',
        'Amorphous structure',
        'Grain boundary',
        'Unit cell',
      ],
      0,
      'A crystal lattice is the repeating pattern of atoms; "unit cell" refers to the smallest repeating unit within it.',
    ],
    [
      'Which of these is a primary measure of a material\'s resistance to permanent deformation under load?',
      [
        'Yield strength',
        'Thermal conductivity',
        'Refractive index',
        'Permeability',
      ],
      0,
      'Yield strength marks the stress threshold beyond which a material deforms permanently. Other properties measure different behavior.',
    ],
    [
      'What phase diagram axis typically represents composition in a binary alloy system?',
      [
        'Horizontal axis',
        'Vertical (y-axis)',
        'Diagonal',
        'Radial',
      ],
      0,
      'Phase diagrams use the horizontal axis for composition and the vertical axis for temperature in standard binary systems.',
    ],
    [
      'Which material class is defined by long-chain organic molecules with repeating units?',
      [
        'Polymers',
        'Ceramics',
        'Metals',
        'Composites',
      ],
      0,
      'Polymers consist of monomer units linked into long chains. Ceramics are inorganic compounds, metals are elemental/alloyed, composites combine matrix and reinforcement.',
    ],
    [
      'What is the primary binding mechanism in ceramic materials?',
      [
        'Ionic and covalent',
        'Metallic bonding',
        'Hydrogen bonding',
        'Van der Waals forces',
      ],
      0,
      'Ceramics rely on ionic and covalent bonds between atoms. Metals use metallic bonding; polymers use covalent backbone with weaker secondary forces.',
    ],
    [
      'In metallurgy, what is an alloy?',
      [
        'A mixture of elements including a metal',
        'A pure metal element',
        'A chemical compound with fixed stoichiometry',
        'A layered structure of different metals',
      ],
      0,
      'An alloy is a metallic mixture (solid solution, intermetallic, or multiphase) designed to improve properties over the base metal.',
    ],
    [
      'Which mechanical property describes a material\'s ability to absorb energy before fracturing?',
      [
        'Toughness',
        'Hardness',
        'Stiffness',
        'Ductility',
      ],
      0,
      'Toughness combines strength and ductility, measuring energy absorbed during fracture. Hardness resists indentation; stiffness resists deformation; ductility is permanent deformation capacity.',
    ],
    [
      'What technique measures the resistance of a material surface to scratching or indentation?',
      [
        'Hardness testing',
        'Tensile testing',
        'Thermal analysis',
        'X-ray diffraction',
      ],
      0,
      'Hardness tests (Vickers, Rockwell, Brinell) quantify surface resistance. Tensile testing measures strength/ductility; thermal analysis tracks phase changes; XRD identifies crystal structure.',
    ],
    [
      'Which of these is a primary application for semiconductors in materials engineering?',
      [
        'Controlling conductivity',
        'Load-bearing structural components',
        'Improving polymer strength',
        'Increasing ceramic toughness',
      ],
      0,
      'Semiconductors (Si, Ge, GaAs) are engineered for controlled electrical properties. The other options describe different material functions.',
    ],
    [
      'What is a composite material?',
      [
        'Two or more materials combined',
        'A single pure element with multiple crystal phases',
        'An alloy with exactly three components',
        'A polymer reinforced with inert fillers only',
      ],
      0,
      'Composites intentionally combine matrix and reinforcement (fibers, particles, etc.) to leverage the strengths of each constituent.',
    ],
    [
      'In materials science, what does the term "grain" refer to?',
      [
        'A single crystal domain',
        'A particle size classification standard',
        'The texture of a polymer surface',
        'A defect in a ceramic structure',
      ],
      0,
      'A grain is one crystalline domain; grains are separated by grain boundaries. Grain size dramatically affects material properties.',
    ],
    [
      'Which of these represents the highest packing density for spheres in a lattice?',
      [
        'FCC or HCP',
        'Simple cubic',
        'Body-centered cubic',
        'Diamond cubic',
      ],
      0,
      'FCC and HCP both achieve ~74% packing density. Simple cubic is ~52%, BCC is ~68%, diamond is ~34%.',
    ],
    [
      'What does "annealing" accomplish in metal processing?',
      [
        'Heating and slow cooling',
        'Rapid cooling to increase hardness',
        'Alloying with carbon to strengthen steel',
        'Polishing the surface to improve appearance',
      ],
      0,
      'Annealing relieves stress, recrystallizes the structure, and softens the metal. Rapid cooling (quenching) hardens; alloying adds strength; polishing is cosmetic.',
    ],
    [
      'Which property describes the tendency of a material to absorb and then release energy when deformed elastically?',
      [
        'Resilience',
        'Brittleness',
        'Malleability',
        'Transparency',
      ],
      0,
      'Resilience is the elastic energy absorption capacity. Brittleness means limited plastic deformation before fracture; malleability is shape-changing ability; transparency is light transmission.',
    ],
    [
      'What structural feature makes polymers flexible compared to metals?',
      [
        'Weak secondary bonds',
        'Lower atomic mass',
        'Lower melting points',
        'Random atom arrangements',
      ],
      0,
      'Polymer chains are held together by weak secondary forces, permitting chain motion. Metals have rigid metallic bonding throughout the structure.',
    ],
  ],
  msc: [
    [
      'What is the significance of the eutectic point on a binary phase diagram?',
      [
        'The lowest-melting composition point',
        'The point of maximum solubility of one metal in another',
        'The composition where mechanical properties are optimized',
        'The critical temperature above which the system becomes a single phase',
      ],
      0,
      'The eutectic is the lowest-melting composition; all constituents crystallize together at constant temperature, forming a characteristic lamellar microstructure.',
    ],
    [
      'In polymer science, what term describes the degree to which polymer chains are arranged in ordered, regular arrays?',
      [
        'Crystallinity',
        'Polymerization',
        'Branching',
        'Cross-linking',
      ],
      0,
      'Crystallinity measures ordered vs. amorphous chain packing; high crystallinity increases strength and melting point. Polymerization is chain growth; branching and cross-linking alter topology.',
    ],
    [
      'Which ceramic fabrication technique involves dissolving precursor materials and forming a gel that is dried and sintered?',
      [
        'Sol-gel synthesis',
        'Dry pressing',
        'Slip casting',
        'Hot pressing',
      ],
      0,
      'Sol-gel produces fine, homogeneous ceramics at low temperatures via precursor gelation. Pressing and casting use preformed powders; hot pressing combines pressure and heat.',
    ],
    [
      'What mechanism allows martensitic transformation in steel to produce hardness?',
      [
        'Diffusionless atomic rearrangement',
        'Nucleation and growth of cementide particles',
        'Grain refinement due to rapid cooling',
        'Precipitation of intermetallic phases',
      ],
      0,
      'Martensite forms via a diffusionless (athermal) shear transformation, trapping carbon atoms and creating distorted, hard crystal structure.',
    ],
    [
      'In fracture mechanics, what does the stress intensity factor (K) characterize?',
      [
        'The severity of the crack-tip field',
        'The total strength of the material',
        'The ductility of the material',
        'The temperature at which fracture occurs',
      ],
      0,
      'K quantifies crack-tip stress concentration; fracture occurs when K exceeds the critical fracture toughness (K_IC). It links material properties to defect size and stress.',
    ],
    [
      'What is the primary advantage of adding reinforcing fibers to a polymer matrix in composites?',
      [
        'Fibers add stiffness, matrix adds toughness',
        'Fibers reduce the overall density of the composite',
        'Fibers prevent the matrix from crystallizing',
        'Fibers increase the transparency of the material',
      ],
      0,
      'Fiber-reinforced composites leverage fiber strength/stiffness with matrix ductility, achieving superior specific (weight-adjusted) properties.',
    ],
    [
      'Which characterization technique uses a beam of X-rays diffracted by crystal planes to identify phases and lattice parameters?',
      [
        'X-ray diffraction (XRD)',
        'Differential scanning calorimetry (DSC)',
        'Transmission electron microscopy (TEM)',
        'Atomic force microscopy (AFM)',
      ],
      0,
      'XRD applies Bragg\'s law to crystal planes, producing diffraction patterns that identify phases and quantify lattice constants. DSC measures thermal events; TEM and AFM image structure.',
    ],
    [
      'What is the Hall effect in semiconductors, and why is it significant?',
      [
        'A transverse voltage from current in a magnetic field',
        'The change in bandgap energy with applied voltage',
        'The generation of light when current flows through a junction',
        'The resistance change at very low temperatures',
      ],
      0,
      'Hall effect (transverse voltage from current × magnetic field) measures charge carrier density and type. It is crucial for characterizing semiconductor doping and mobility.',
    ],
    [
      'In alloy design, what is a solid solution strengthening mechanism?',
      [
        'Solute atoms distorting the lattice',
        'Precipitation of a second phase at grain boundaries',
        'Formation of new crystal phases with different structures',
        'Reduction of grain size by pinning grain boundaries',
      ],
      0,
      'Solute atoms create local lattice strains that block dislocation slip, raising yield strength without adding discrete particles.',
    ],
    [
      'What property of a polymer is measured by tensile testing at a rate much slower than normal to reveal time-dependent behavior?',
      [
        'Creep behavior',
        'Impact strength',
        'Tear resistance',
        'Surface hardness',
      ],
      0,
      'Creep testing applies constant stress at low strain rate to quantify permanent deformation over time—critical for long-term load-bearing polymers.',
    ],
    [
      'Which nanomaterial form consists of single-atom-thick sheets of carbon arranged in a hexagonal lattice?',
      [
        'Graphene',
        'Carbon nanotubes',
        'Fullerenes',
        'Nanoparticles',
      ],
      0,
      'Graphene is a 2D allotrope with exceptional strength, conductivity, and stiffness. Nanotubes are 1D rolled graphene; fullerenes are 0D cage molecules; nanoparticles are bulk spheres.',
    ],
    [
      'What is the role of a dislocation in plastic deformation of crystalline materials?',
      [
        'A line defect that moves through the lattice',
        'A point defect created by missing atoms at lattice sites',
        'A grain boundary separating two crystal orientations',
        'A stacking fault in a layered structure',
      ],
      0,
      'Dislocations (line defects) move via shear, enabling plastic flow at stresses far below theoretical limits. Other defects affect behavior differently.',
    ],
    [
      'In materials characterization, what phenomenon does differential scanning calorimetry (DSC) measure?',
      [
        'Heat flow as temperature changes',
        'The crystalline structure of the sample',
        'The surface roughness and topography',
        'The electrical conductivity vs. temperature',
      ],
      0,
      'DSC measures heat flux (endo/exothermic) to detect melting, crystallization, glass transitions, and decomposition. XRD reveals structure; AFM maps topology; other methods measure conductivity.',
    ],
    [
      'What is the primary mechanism by which grain boundary engineering improves fracture toughness in ceramics?',
      [
        'Controlled intergranular crystallography',
        'Increasing the total number of grains in the material',
        'Adding small amounts of ductile phases at boundaries',
        'Eliminating all grain boundaries via single-crystal growth',
      ],
      0,
      'Tailored grain boundary types (e.g., higher-order twin boundaries) deflect and blunt cracks, increasing energy absorption before failure.',
    ],
    [
      'What is the significance of the Schmid factor in determining the ease of dislocation slip on a particular slip system?',
      [
        'The resolved shear stress, τ = σ·cos(φ)·cos(λ)',
        'It measures the total dislocation density in the material',
        'It controls the thermal stability of dislocations',
        'It determines the rate of dislocation nucleation',
      ],
      0,
      'Schmid factor m = cos(φ)cos(λ) predicts slip system activation; highest m systems yield first. Critical resolved shear stress τ_CRSS is independent of σ magnitude.',
    ],
  ],
  phd: [
    [
      'What does the Gibbs free energy surface reveal when plotted as a function of composition and temperature in a ternary phase diagram?',
      [
        'Thermodynamic phase stability',
        'The mechanical properties of each phase as a function of composition',
        'The kinetic pathway by which one phase transforms into another',
        'The electrical conductivity surface of the alloy system',
      ],
      0,
      'G(X,T) surfaces identify stable phases; phase boundaries emerge from common tangent points. This is the thermodynamic foundation of phase diagrams.',
    ],
    [
      'In materials degradation, what is the role of electrochemical potential in pitting corrosion of passivated metals?',
      [
        'The passive film breaks down beyond a critical potential',
        'The potential determines the rate of uniform corrosion across the surface',
        'The electrochemical potential controls the thickness of the oxide layer',
        'It directly sets the mechanical strength of the corroded material',
      ],
      0,
      'Pitting initiates when applied or localized potential exceeds Eₚᵢₜ, breaking down the passivating oxide film and triggering self-propagating corrosion.',
    ],
    [
      'What is the origin of the glass transition temperature (T_g) in amorphous polymers, and how does it differ from crystalline melting?',
      [
        'T_g is kinetic, Tₘ is a true phase change',
        'T_g occurs at higher temperatures than Tₘ in all polymers',
        'T_g requires a crystal structure to be defined',
        'Both T_g and Tₘ measure the same thermodynamic event',
      ],
      0,
      'T_g is a kinetic transition (chain freezing/unfreezing); Tₘ is a true phase transition melting ordered crystals. They reflect different physical phenomena.',
    ],
    [
      'In semiconductor physics, what determines the width of the depletion region in a p-n junction at thermal equilibrium?',
      [
        'The built-in potential and doping levels',
        'The applied external voltage',
        'The thermal energy of the charge carriers',
        'The bandgap energy of the material',
      ],
      0,
      'Depletion width W ∝ √(V_bi/[eNₐ+eN_D]) from Poisson\'s equation; built-in potential (ln(Nₐ N_D / nᵢ²)) and doping levels control it.',
    ],
    [
      'What is the significance of the Burger\'s vector in characterizing dislocations, and how does it relate to the dislocation type?',
      [
        'The magnitude and direction of lattice distortion',
        'It determines the energy required to form the dislocation',
        'It controls whether the dislocation can move via climb or glide',
        'It measures the distance the dislocation has moved through the crystal',
      ],
      0,
      'Burger\'s vector b characterizes the defect; for edge b ⊥ line direction, for screw b ∥ line direction. b magnitude indicates lattice distortion and energy.',
    ],
    [
      'In composite mechanics, what does the rule of mixtures predict, and under what loading condition is it most accurate?',
      [
        'A weighted average of constituent properties',
        'The failure stress of the composite regardless of loading direction',
        'The thermal expansion coefficient of all fiber-reinforced composites',
        'The electrical conductivity of ceramic matrix composites',
      ],
      0,
      'Rule of mixtures: E_c = V_f E_f + Vₘ Eₘ is accurate for longitudinal loading where loads directly transfer through fibers.',
    ],
    [
      'What mechanisms dominate fracture toughness enhancement in transformation-toughened zirconia ceramics?',
      [
        'A stress-induced phase transformation',
        'Plastic deformation of grain boundaries',
        'Microcracking ahead of the crack tip',
        'Precipitation of a metallic phase',
      ],
      0,
      'Zirconia\'s tetragonal phase is metastable; crack-tip stress triggers transformation to monoclinic, creating volume expansion that compresses cracks and dissipates energy.',
    ],
    [
      'In nanomaterials, what is the role of the surface-to-volume ratio in altering the properties of a material relative to the bulk?',
      [
        'Surface energy dominates at small sizes',
        'Surface effects are negligible below 100 nm',
        'Surface atoms are chemically identical to bulk atoms',
        'Nanomaterials always have higher melting points than bulk',
      ],
      0,
      'As size decreases, S/V increases; surface energy becomes dominant, lowering Tₘ (Gibbs-Thomson), increasing sintering/diffusion rates, altering thermodynamic stability.',
    ],
    [
      'What is the physical basis of the Griffith criterion for brittle fracture, and what does it predict?',
      [
        'Released strain energy equals surface energy',
        'Fracture is purely stress-controlled and independent of flaw size',
        'All materials fail at the same applied stress if cracks are present',
        'Ductile deformation always occurs before fracture',
      ],
      0,
      'Griffith: stress required for fracture σ = √(2Eγ/πa) decreases with flaw size a. Energy balance (elastic release vs. surface creation) predicts instability.',
    ],
    [
      'In alloy thermodynamics, what does a negative heat of mixing indicate, and how does it affect phase stability?',
      [
        'Favorable atomic interactions that stabilize mixing',
        'Repulsive interactions that always cause phase separation',
        'No effect on phase stability or the phase diagram shape',
        'An indicator that the alloy will be amorphous',
      ],
      0,
      'Negative ΔHₘᵢₓ favors inter-atomic bonding and homogeneous mixing; phase separation occurs at larger |ΔS|. Positive ΔHₘᵢₓ drives phase splitting.',
    ],
    [
      'What information does transmission electron microscopy (TEM) provide that scanning electron microscopy (SEM) cannot?',
      [
        'Atomic-resolution crystal structure and defects',
        'Higher operating voltages for deeper penetration',
        'Better color imaging of sample surfaces',
        'More rapid analysis of large sample areas',
      ],
      0,
      'TEM transmits electrons through thin samples, revealing atomic structure, dislocations, and interfaces (nm–Å resolution); SEM images surfaces topographically (nm resolution).',
    ],
    [
      'In biomaterials, what is osseointegration, and what material and surface properties enhance it?',
      [
        'Direct bone-implant contact via bioactive surfaces',
        'Implant materials that are completely inert and non-reactive',
        'Any metal implant that achieves long-term stability in the body',
        'The encapsulation of an implant in scar tissue',
      ],
      0,
      'Osseointegration requires bioactive surfaces (TiO₂, hydroxyl groups, calcium phosphate) and roughness (micron-scale) for osteoblast attachment and bone formation.',
    ],
    [
      'What is the theoretical yield strength of a perfect crystal, and how does real yield strength compare due to dislocations?',
      [
        'Real yield strength is far lower, due to dislocations',
        'Actual yield strength always exceeds theoretical strength',
        'Dislocations increase the required stress to deform the material',
        'Perfect crystals are impossible, so theory is irrelevant',
      ],
      0,
      'Perfect crystal: σ_th ≈ G/30 (all bonds break simultaneously). Dislocations allow shear via rearrangement, reducing observed σ_y by 1–2 orders of magnitude.',
    ],
    [
      'In processing-structure-property relationships, how does rapid solidification alter the microstructure and extend solid solubility limits in alloys?',
      [
        'It suppresses diffusion, trapping excess solute',
        'Rapid cooling always produces equilibrium phases',
        'Cooling rate has no effect on solid solubility',
        'Only grain size is affected; composition remains equilibrium',
      ],
      0,
      'Rapid solidification kinetically suppresses diffusion; solute atoms are trapped, exceeding equilibrium solubility and forming non-equilibrium solid solutions and metastable phases.',
    ],
    [
      'What is the relationship between stacking fault energy (SFE) and dislocation character in FCC metals?',
      [
        'Low SFE favors twinning over cross-slip',
        'SFE determines the slip direction in BCC metals',
        'High SFE always leads to brittle fracture',
        'SFE is independent of alloy composition',
      ],
      0,
      'Low SFE (e.g., Cu, Ag alloys) widens partial dislocation separation, inhibiting cross-slip and promoting twinning; high SFE (Al, Ni) favors easy cross-slip and hardening.',
    ],
  ],
  professor: [
    [
      'In thermodynamic modeling of multicomponent alloys, what is the physical and mathematical significance of the excess Gibbs energy (G^ex) in determining phase stability and ordering?',
      [
        'Deviations from ideal mixing behavior',
        'G^ex is always zero in all real materials',
        'G^ex only affects the entropy term, not enthalpy',
        'G^ex is purely empirical with no physical basis',
      ],
      0,
      'G^ex (non-ideal contribution) arises from atomic interactions; its form determines phase diagram topology—miscibility gaps, consolute points, and order-disorder transitions.',
    ],
    [
      'What does the Cottrell atmosphere model explain in ferrous metallurgy, and how does it relate to dynamic strain aging and the Portevin–Le Chatelier effect?',
      [
        'Dynamic locking and unlocking of solute atmospheres',
        'The Cottrell model only applies to static strength, not dynamic behavior',
        'It predicts that dynamic strain aging always softens the material',
        'Cottrell atmospheres are irrelevant to modern alloys',
      ],
      0,
      'Cottrell atmospheres (solute-dislocation clouds) dynamically repin dislocations; competition between diffusion-assisted unpinning and re-pinning produces PLC effect (flow serrations).',
    ],
    [
      'In fracture mechanics, what distinguishes K-dominated (linear elastic fracture mechanics, LEFM) from J-integral-dominated (elastic-plastic fracture mechanics, EPFM) regimes, and when does each apply?',
      [
        'LEFM applies for small plastic zones, J-integral for large ones',
        'K and J are mathematically equivalent and always give the same results',
        'J-integral is only used for ceramics, K for metals',
        'LEFM is the only valid fracture criterion for all materials',
      ],
      0,
      'LEFM: small plastic zone (B, a >> p_y); valid when σ_Y, elastic material. EPFM: large plastic zone; J-integral captures nonlinear path-independence.',
    ],
    [
      'What role do coherency strains play in precipitate hardening mechanisms, and how does overaging reduce strength?',
      [
        'Coherency strains that weaken as particles coarsen',
        'Coherency strains always promote plastic deformation',
        'Overaging increases hardness indefinitely',
        'Precipitate size has no effect on dislocation hardening',
      ],
      0,
      'Coherent strains maximize cutting resistance; as particles coarsen via diffusion, elastic strains decrease and Orowan spacing increases, reducing hardness.',
    ],
    [
      'In surface thermodynamics, what is the relationship between surface energy (γ), surface excess, and interfacial segregation, and why do certain elements preferentially segregate?',
      [
        'Weakly-bonded atoms segregate to lower interfacial energy',
        'Surface energy is always equal to bulk energy',
        'Segregation has no thermodynamic driving force',
        'All elements segregate at equal rates regardless of properties',
      ],
      0,
      'Segregation minimizes G_interface; elements with weak bulk cohesion and high surface affinity (low γ, favorable ΔH_ads) preferentially adsorb at defects.',
    ],
    [
      'What is the origin of the piezoelectric effect in crystals, and why is it forbidden in materials with inversion symmetry?',
      [
        'Strain-induced ion displacement in non-centrosymmetric crystals',
        'Piezoelectricity arises from metallic bonding alone',
        'Inversion symmetry enhances the piezoelectric response',
        'The piezoelectric effect is purely electronic with no ion movement',
      ],
      0,
      'Piezoelectricity: strain breaks symmetry, creating uncompensated charge. Inversion symmetry makes strain equivalent to -strain, so net dipole = 0.',
    ],
    [
      'In the theory of martensitic phase transformations, what is the lattice deformation gradient, and how does the Bain strain relate to the lattice correspondence and orientation relationship?',
      [
        'Bain strain plus rigid rotation of the lattice',
        'Martensitic transformation is purely reconstructive, with no lattice deformation',
        'The lattice correspondence is arbitrary and unrelated to OR',
        'Bain strain and habit plane are independent parameters',
      ],
      0,
      'F = R·U; Bain strain (lattice stretch toward martensite) + rigid rotation R determine habit plane (invariant plane strain condition) and crystal OR.',
    ],
    [
      'What is the mechanism of crack bridging in fiber-reinforced composites, and how does fiber pullout work length contribute to fracture toughness?',
      [
        'Unbroken fibers bridging the crack dissipate energy',
        'Fiber bridging reduces composite stiffness',
        'Pullout work is always negligible compared to matrix toughness',
        'Fibers must break for the composite to fail',
      ],
      0,
      'Bridged fibers carry σ_f = τ√(2ρ_f λ) (shear lag); L_eff pullout length and interfacial friction τ control Wₚᵤₗₗₒᵤₜ, directly raising K_IC or G_IC.',
    ],
    [
      'In semiconductor band structure engineering, what does the concept of "band gap engineering" through heterojunctions accomplish, and what role does strain play?',
      [
        'Strained heterostructures shift bandgaps and offsets',
        'Bandgap engineering only applies to elemental semiconductors',
        'Strain always degrades semiconductor performance',
        'Band offsets are determined solely by doping levels',
      ],
      0,
      'Epitaxial strain in mismatched heterostructures shifts E_g and ΔE_c/ΔEᵥ offsets, enabling tailored band alignment for devices (LEDs, lasers, thermoelectrics).',
    ],
    [
      'What is the Mullins effect in elastomers, and what molecular mechanisms govern stress-softening and hysteresis in filled polymers?',
      [
        'Breakdown of filler-polymer bonds and agglomerates',
        'The Mullins effect only occurs in unfilled elastomers',
        'Filled polymers always show constant stress-strain behavior upon reloading',
        'Hysteresis is purely elastic with no viscous contribution',
      ],
      0,
      'Mullins effect: cyclic loading ruptures filler-polymer interfaces and agglomerates; lower modulus on reload due to irreversible structure changes and viscous dissipation.',
    ],
    [
      'In high-temperature creep of ceramics and metals, what is the physical basis of diffusional creep (Nabarro-Herring and Coble mechanisms), and how do they scale with stress and grain size?',
      [
        'Stress-driven vacancy diffusion',
        'Diffusional creep is always faster than dislocation creep',
        'Grain size has no effect on creep rate',
        'Creep is purely elastic deformation at high temperature',
      ],
      0,
      'N-H (interior diffusion) and Coble (grain-boundary diffusion) scale with σ and inverse power of d; rates ∝ D(T)σ. Dominates at low σ, high T.',
    ],
    [
      'What is the physical significance of the Debye temperature (Θ_D) in determining lattice and thermal transport properties, and how is it related to materials\' mechanical properties?',
      [
        'It scales with stiffness and sound velocity',
        'Debye temperature only affects optical properties',
        'High Θ_D always means better thermal conductivity',
        'Θ_D is independent of elastic properties',
      ],
      0,
      'Θ_D ≈ ℏω_D/k_B ∝ vₛ; high Θ_D indicates stiff lattice (high E, vₛ). Phonon scattering and mfp control κ independently.',
    ],
    [
      'In heterogeneous nucleation, what role does the contact angle and surface energy difference play in reducing the critical nucleus size compared to homogeneous nucleation?',
      [
        'A lower contact angle reduces the critical nucleus size',
        'Contact angle only affects wetting, not nucleation',
        'Homogeneous and heterogeneous nucleation have identical critical sizes',
        'Surface energy differences are always negligible for nucleation',
      ],
      0,
      'Critical radius: r* = 2γₛₗ cosθ/(ρΔg); as θ → 0, r* → min; activation energy ΔG* ∝ f(θ)·γ³/ΔG², favoring heterogeneous paths.',
    ],
    [
      'What is the mechanism and significance of grain growth in nanocrystalline materials, and why is the Zener pinning force insufficient to stabilize nm-scale grains at elevated temperature?',
      [
        'Zener pinning weakens as grain size shrinks toward nm scale',
        'Nanocrystalline materials never grow, regardless of temperature',
        'Grain growth is controlled purely by dislocation density, not grain boundaries',
        'Zener pinning becomes stronger at smaller grain sizes',
      ],
      0,
      'Grain growth: dE/dd ∝ -2γ/d (reduces G); Zener: F_Z = Z·r/d weakens as d shrinks and precipitate density falls; thermal activation overwhelms weak pinning.',
    ],
    [
      'In the context of computational materials science, what is density functional theory (DFT), and what approximation (e.g., LDA, GGA) fundamentally limits its predictive accuracy for ground-state properties?',
      [
        'The exchange-correlation functional is approximated',
        'DFT directly solves the full Schrödinger equation without approximation',
        'LDA and GGA are exact functionals with no errors',
        'DFT cannot predict structural properties, only electronic ones',
      ],
      0,
      'DFT basis: ground-state E uniquely determined by n(r); Kohn-Sham is exact but E_xc[n] is unknown. LDA/GGA approximate E_xc, introducing systematic errors in band structure and barriers.',
    ],
  ],
};
